import {
  Anchor,
  Box,
  Button,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { ModalProps as MantineModalProps } from '@mantine/core/lib/Modal/Modal';
import { last, split } from 'lodash/fp';
import { PDFDocumentProxy } from 'pdfjs-dist';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack5';
import { useEffectOnce } from 'react-use';

import { OrderType } from '@portals/api/partners';
import { ReactComponent as Export } from '@portals/icons/linear/export.svg';
import { ReactComponent as Printer } from '@portals/icons/linear/printer.svg';
import { VerticalScrollBar } from '@portals/scrollbar';
import { downloadFile } from '@portals/utils';

import { ReactComponent as TaxIcon } from '../../assets/img/choose-tax-group.svg';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export interface PurchaseOrderFilePreviewProps {
  order: OrderType;
  onSubmit: () => void;
  onCancel: () => void;
  setIsDownloaded: () => void;
  isDownloaded: boolean;
}

function PurchaseOrderFilePreview({
  order,
  onSubmit,
  onCancel,
  setIsDownloaded,
  isDownloaded,
}: PurchaseOrderFilePreviewProps) {
  const { classes } = useStyles();

  const [isFailedToLoad, setIsFailedToLoad] = useState(false);
  const [PDFDocumentProxy, setPDFDocumentProxy] =
    useState<PDFDocumentProxy>(null);

  const ref = useRef();
  const fileFormat = last(split('.', order?.purchase_order_file_url));

  const onDownload = useCallback(() => {
    if (fileFormat !== 'pdf' && !isDownloaded) {
      setIsDownloaded();
    }

    downloadFile(order.purchase_order_file_url, 'purchase_order');
  }, [
    fileFormat,
    isDownloaded,
    order.purchase_order_file_url,
    setIsDownloaded,
  ]);

  const onPrint = async () => {
    const data = await PDFDocumentProxy.getData();
    const blob = new Blob([data], { type: `application/pdf` });

    window.open(URL.createObjectURL(blob));
  };

  useEffectOnce(() => {
    if (fileFormat !== 'pdf' && !isDownloaded) {
      onDownload();
    }
  });

  const content = useMemo(() => {
    if (fileFormat !== 'pdf' || isFailedToLoad) {
      return (
        <Center>
          <Stack align="center">
            <TaxIcon width={158} height={158} />

            <Stack align="center">
              <Text size="sm" color="gray.8">
                No preview available. Downloading purchase order....
              </Text>

              <Stack spacing={0} align="center">
                <Text size="xs" color="gray.5">
                  Download should start automatically in a few seconds.
                </Text>

                <Text size="xs" color="gray.5">
                  If it doesn't,{' '}
                  <Anchor
                    onClick={onDownload}
                    data-testid="no-preview-download-po-file-btn"
                  >
                    please click here
                  </Anchor>
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Center>
      );
    }

    return (
      <>
        <LoadingOverlay visible={!PDFDocumentProxy?.numPages} />

        <Box className={classes.documentWrapper}>
          <VerticalScrollBar height="100%">
            <Document
              ref={ref}
              file={order.purchase_order_file_url}
              loading={null}
              onLoadSuccess={setPDFDocumentProxy}
              onLoadError={() => setIsFailedToLoad(true)}
            >
              {Array.from(
                new Array(PDFDocumentProxy?.numPages || 0),
                (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    scale={1.5}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                )
              )}
            </Document>
          </VerticalScrollBar>
        </Box>
      </>
    );
  }, [
    PDFDocumentProxy?.numPages,
    classes.documentWrapper,
    fileFormat,
    isFailedToLoad,
    onDownload,
    order.purchase_order_file_url,
  ]);

  return (
    <Stack className={classes.content} spacing={0} p={0}>
      <Group position="right" className={classes.actions} spacing="xs">
        <Button
          variant="white"
          leftIcon={<Export />}
          color="blue_gray"
          onClick={onDownload}
          data-testid="download-po-file-btn"
        >
          Download
        </Button>

        {fileFormat === 'pdf' && !isFailedToLoad ? (
          <Button
            variant="white"
            leftIcon={<Printer />}
            color="blue_gray"
            onClick={onPrint}
            data-testid="print-po-file-btn"
          >
            Print
          </Button>
        ) : null}
      </Group>

      {content}

      <Group position="right" className={classes.footer}>
        <Button
          variant="default"
          onClick={onCancel}
          data-testid="cancel-po-approval-btn"
        >
          Cancel
        </Button>

        <Button onClick={onSubmit} data-testid="submit-po-approval-btn">
          Approve Purchase Order
        </Button>
      </Group>
    </Stack>
  );
}

export function PurchaseOrderFilePreviewWrapper({
  onCancel,
  onSubmit,
  setIsDownloaded,
  isDownloaded,
  order,
}: PurchaseOrderFilePreviewProps) {
  return (
    <Modal
      opened
      onClose={onCancel}
      withCloseButton={false}
      styles={modalStyles}
      size={1092}
    >
      {order ? (
        <PurchaseOrderFilePreview
          order={order}
          onSubmit={onSubmit}
          onCancel={onCancel}
          setIsDownloaded={setIsDownloaded}
          isDownloaded={isDownloaded}
        />
      ) : (
        <LoadingOverlay visible />
      )}
    </Modal>
  );
}

const useStyles = createStyles((theme) => ({
  content: {
    display: 'grid',
    gridTemplateRows: 'min-content 1fr min-content',
    gridTemplateColumns: '1fr',
    height: '80vh',
  },
  actions: {
    padding: theme.spacing.xl,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  documentWrapper: {
    height: '100%',
    display: 'grid',
    gridTemplateRows: '100%',
  },
  footer: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.xl,
  },
}));

const modalStyles: MantineModalProps['styles'] = (theme) => ({
  root: {
    overflow: 'hidden',
  },
  modal: {
    minWidth: 900,
    minHeight: 330,
    overflow: 'hidden',
    padding: '0 !important',
  },
  header: {
    display: 'none',
  },
});
